import React from "react";
import {
  Badge,
  Button,
  Checkbox,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { ViewColumn } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { ColumnDef } from "./types";

interface Props<T> {
  columns: ColumnDef<T>[];
  isVisible: (id: string) => boolean;
  onToggle: (id: string) => void;
  onReset: () => void;
}

/**
 * Lets the user re-enable columns the breakpoint auto-hid, which is what makes
 * narrow-screen hiding acceptable: nothing is ever unreachable, it is just off
 * by default. `essential` columns are omitted entirely rather than shown
 * disabled — a checkbox that cannot be unchecked is noise.
 */
export default function ColumnPicker<T>({
  columns,
  isVisible,
  onToggle,
  onReset,
}: Props<T>) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const optional = columns.filter((c) => c.priority !== "essential");
  if (optional.length === 0) return null;

  const hiddenCount = optional.filter((c) => !isVisible(c.id)).length;

  return (
    <>
      <Tooltip title={t("dataTable.chooseColumns")}>
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label={t("dataTable.chooseColumns")}
        >
          <Badge badgeContent={hiddenCount} color="primary">
            <ViewColumn />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {optional.map((column) => (
          <MenuItem
            key={column.id}
            onClick={() => onToggle(column.id)}
            dense
          >
            <Checkbox checked={isVisible(column.id)} size="small" />
            <ListItemText primary={t(column.labelKey)} />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem dense disableRipple>
          <Button size="small" fullWidth onClick={onReset}>
            {t("dataTable.resetColumns")}
          </Button>
        </MenuItem>
      </Menu>
    </>
  );
}
